import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import MediaCard from "@/components/feature/MediaCard";
import Pagination from "@/components/feature/Pagination";
import FilterPanel, { type CategoryFilterState } from "@/pages/category/components/FilterPanel";
import { getMediaByType, filterMedia, PAGE_SIZE } from "@/utils/media";
import { movieGenres, tvGenres } from "@/mocks/media";
import { useSEO } from "@/hooks/useSEO";
import type { MediaType } from "@/types/media";

interface CategoryViewProps {
  type: MediaType;
}

const defaultFilter: CategoryFilterState = {
  genre: "",
  year: 0,
  region: "",
  season: 0,
  sort: "updated",
};

export default function CategoryView({ type }: CategoryViewProps) {
  const [filter, setFilter] = useState<CategoryFilterState>(defaultFilter);
  const [page, setPage] = useState(1);

  const isMovie = type === "movie";
  const title = isMovie ? "电影分类" : "电视剧分类";
  const genres = isMovie ? movieGenres : tvGenres;
  const source = getMediaByType(type);

  useSEO({
    title: `${title} - 西瓜影院`,
    description: isMovie
      ? `西瓜影院电影分类页，汇集动作、科幻、剧情、悬疑等题材的热门电影与经典佳片，支持按类型、年份、地区筛选与多种排序。`
      : `西瓜影院电视剧分类页，收录都市、奇幻、律政、历史等题材的热门剧集，支持按类型、年份、地区与季数筛选。`,
    keywords: isMovie ? "电影分类,在线电影,高清电影,西瓜影院" : "电视剧分类,在线电视剧,热播剧集,西瓜影院",
  });

  const filtered = useMemo(() => filterMedia(source, filter), [source, filter]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const updateFilter = (next: CategoryFilterState) => {
    setFilter(next);
    setPage(1);
  };

  const reset = () => {
    setFilter(defaultFilter);
    setPage(1);
  };

  return (
    <div className="mx-auto px-4 py-8 md:px-8 md:py-10">
      <nav aria-label="面包屑" className="mb-5 flex flex-wrap items-center gap-2 text-sm text-foreground-500">
        <Link to="/" className="transition-colors hover:text-primary-400">
          首页
        </Link>
        <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
        <span className="text-foreground-200">{title}</span>
      </nav>

      <header className="mb-6">
        <h1 className="font-heading text-2xl font-700 text-foreground-950 md:text-3xl">
          {title} - 西瓜影院
        </h1>
        <p className="mt-2 max-w-3xl text-sm leading-6 text-foreground-400">
          {isMovie
            ? "汇集动作、科幻、剧情、悬疑等题材的热门电影与经典佳片，支持按类型、年份、地区筛选，并可按更新时间、热度或评分排序，快速找到你想看的那一部。"
            : "收录都市、奇幻、律政、历史等题材的热门电视剧，支持按类型、年份、地区与季数筛选，让你轻松追剧不迷路。"}
        </p>
      </header>

      <FilterPanel
        genres={genres}
        showSeason={!isMovie}
        value={filter}
        onChange={updateFilter}
        onReset={reset}
      />

      <div className="mt-6 flex items-center justify-between text-sm text-foreground-500">
        <p>
          共找到 <span className="font-700 text-accent-400">{filtered.length}</span> 部作品
        </p>
        <p className="hidden sm:block">
          第 {safePage} / {totalPages} 页
        </p>
      </div>

      {pageItems.length > 0 ? (
        <>
          <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
            {pageItems.map((item) => (
              <MediaCard key={item.id} item={item} />
            ))}
          </div>
          <Pagination current={safePage} total={totalPages} onChange={setPage} />
        </>
      ) : (
        <div className="mt-6 flex flex-col items-center justify-center rounded-lg border border-background-200 bg-background-100 py-16 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-emotion-unhappy-line text-3xl" aria-hidden="true"></i>
          </span>
          <h2 className="mt-4 font-700 text-foreground-950">没有找到符合条件的作品</h2>
          <p className="mt-2 text-sm text-foreground-500">试试调整筛选条件，或重置后查看更多内容。</p>
          <button
            type="button"
            onClick={reset}
            className="mt-5 inline-flex h-10 items-center gap-2 rounded-md bg-primary-500 px-5 text-sm font-500 text-background-950 transition-colors hover:bg-primary-600"
          >
            <i className="ri-refresh-line text-base" aria-hidden="true"></i>
            重置筛选条件
          </button>
        </div>
      )}
    </div>
  );
}